import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cmdbApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import type { FileFormat, CmdbInboundProfile, CmdbOutboundProfile } from '@/api/types'

// ── Template helpers ──────────────────────────────────────────────────────────

const DEFAULT_INVENTORY_COLS = [
  'external_id', 'inventory_name', 'environment', 'criticality',
  'technology_owner', 'business_owner', 'security_owner', 'business_unit', 'department',
  'lifecycle_stage', 'data_classification', 'tags',
]

const SOURCE_COLS = [
  'source_type', 'source_url', 'source_name', 'inventory_external_id', 'owner_email', 'lifecycle_status',
]

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function downloadCsvTemplate(profile?: CmdbInboundProfile) {
  const inventoryCols = profile?.fields.length
    ? profile.fields.map(f => f.cmdb_field)
    : DEFAULT_INVENTORY_COLS
  const allCols = ['record_type', ...inventoryCols, ...SOURCE_COLS]
  const invSample = ['inventory', ...inventoryCols.map((_, i) => i === 0 ? 'ci-001' : ''), ...SOURCE_COLS.map(() => '')]
  const srcSample = ['source', ...inventoryCols.map(() => ''), 'git', 'https://github.com/org/repo', 'repo-name', 'ci-001', 'owner@example.com', 'active']
  const rows = [allCols, invSample, srcSample]
  const csv = rows.map(r => r.map(v => v.includes(',') ? `"${v}"` : v).join(',')).join('\n')
  downloadBlob(csv, 'cmdb_import_template.csv', 'text/csv')
}

function downloadJsonTemplate(profile?: CmdbInboundProfile) {
  const inventoryCols = profile?.fields.length
    ? profile.fields.map(f => f.cmdb_field)
    : DEFAULT_INVENTORY_COLS
  const inventorySample = Object.fromEntries(
    inventoryCols.map((col, i) => [col, i === 0 ? 'ci-001' : ''])
  )
  const tpl = {
    schema_version: '2.0',
    import_type: 'inbound',
    import_mode: 'upsert',
    inventories: [inventorySample],
    sources: [
      {
        source_type: 'git',
        source_url: 'https://github.com/org/repo',
        source_name: 'repo-name',
        inventory_external_id: 'ci-001',
        owner_email: 'owner@example.com',
        lifecycle_status: 'active',
      },
    ],
  }
  downloadBlob(JSON.stringify(tpl, null, 2), 'cmdb_import_template.json', 'application/json')
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-surface-light dark:bg-[#0d1b2a] rounded-2xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-5 ${className}`}>
      {children}
    </div>
  )
}

function ProfileSelect({
  label,
  profiles,
  value,
  onChange,
  placeholder = 'Select a profile…',
}: {
  label: string
  profiles: { id: string; name: string; is_system: boolean }[]
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <option value="">{placeholder}</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.is_system ? ' (system)' : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

function ImportResultCard({
  result,
}: {
  result: import('@/api/types').CmdbImportResult
}) {
  const hasErrors = (result.errors ?? []).length > 0
  return (
    <div
      className={`rounded-xl p-4 border space-y-1 ${
        hasErrors
          ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800'
          : 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
      }`}
    >
      <p className="text-sm font-medium text-text-light dark:text-text-dark">
        {result.inventories_processed} inventories · {result.sources_processed} sources processed
        {result.inventories_created != null && (
          <span className="ml-2 text-xs text-subtle-light dark:text-subtle-dark">
            ({result.inventories_created} created, {result.inventories_updated ?? 0} updated)
          </span>
        )}
      </p>
      {(result.errors ?? []).map((e, i) => (
        <p key={i} className="text-xs text-error">
          {e}
        </p>
      ))}
    </div>
  )
}

// ── Import tab ────────────────────────────────────────────────────────────────

function ImportTab({
  inboundProfiles,
}: {
  inboundProfiles: CmdbInboundProfile[]
}) {
  const { currentOrgId: orgId } = useOrgStore()
  const queryClient = useQueryClient()
  const [profileId, setProfileId] = useState('')
  const [method, setMethod] = useState<'file' | 'json'>('file')

  useEffect(() => {
    if (!profileId) {
      const generic = inboundProfiles.find(p => p.is_system && p.name === 'Generic')
      if (generic) setProfileId(generic.id)
    }
  }, [inboundProfiles, profileId])

  const selectedProfile = inboundProfiles.find(p => p.id === profileId)

  const [file, setFile] = useState<File | null>(null)
  const [jsonBody, setJsonBody] = useState('')
  const [fileResult, setFileResult] = useState<import('@/api/types').CmdbImportResult | null>(null)
  const [jsonResult, setJsonResult] = useState<import('@/api/types').CmdbImportResult | null>(null)
  const [fileError, setFileError] = useState('')
  const [jsonError, setJsonError] = useState('')

  const invalidateAfterImport = () => {
    queryClient.invalidateQueries({ queryKey: ['inventories', orgId] })
    queryClient.invalidateQueries({ queryKey: ['sources', orgId] })
  }

  const fileMutation = useMutation({
    mutationFn: (f: File) => cmdbApi.standaloneImportFile(orgId, f, profileId || undefined),
    onSuccess: (r) => { setFileResult(r); setFileError(''); invalidateAfterImport() },
    onError: (err: Error) => { setFileError(err.message); setFileResult(null) },
  })

  const jsonMutation = useMutation({
    mutationFn: (body: unknown) => cmdbApi.standaloneImportJson(orgId, body),
    onSuccess: (r) => { setJsonResult(r); setJsonError(''); invalidateAfterImport() },
    onError: (err: Error) => { setJsonError(err.message); setJsonResult(null) },
  })

  const handleJsonImport = () => {
    try {
      const parsed = JSON.parse(jsonBody) as Record<string, unknown>
      if (profileId) parsed.mapping_profile_id = profileId
      parsed.import_mode = 'upsert'
      jsonMutation.mutate(parsed)
    } catch {
      setJsonError('Invalid JSON — check syntax and try again.')
    }
  }

  return (
    <div className="space-y-5">
      {/* Config row */}
      <Card>
        <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-4">Configuration</h3>
        <div className="max-w-sm space-y-4">
          <ProfileSelect
            label="Inbound Profile *"
            profiles={inboundProfiles}
            value={profileId}
            onChange={setProfileId}
          />
        </div>
      </Card>

      {/* Template download */}
      <Card>
        <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-1">Download Template</h3>
        <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
          Use these templates as a starting point. Inventory rows and source rows can be mixed in the same CSV file — the{' '}
          <code className="font-mono">record_type</code> column distinguishes them.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadCsvTemplate(selectedProfile)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-text-light dark:text-text-dark hover:bg-primary/5 transition-colors"
          >
            <span className="material-icons text-sm">download</span>
            CSV Template
          </button>
          <button
            onClick={() => downloadJsonTemplate(selectedProfile)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-text-light dark:text-text-dark hover:bg-primary/5 transition-colors"
          >
            <span className="material-icons text-sm">download</span>
            JSON Template
          </button>
        </div>
      </Card>

      {/* Method toggle */}
      <div className="flex rounded-xl overflow-hidden w-fit">
        {(['file', 'json'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            className={`px-4 py-2 text-xs font-medium transition-colors ${
              method === m
                ? 'bg-primary text-white'
                : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
            }`}
          >
            {m === 'file' ? 'File Upload' : 'JSON Paste'}
          </button>
        ))}
      </div>

      {/* File upload */}
      {method === 'file' && (
        <Card>
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-4">Upload File</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">
                File (CSV / JSON / XML / XLS / XLSX)
              </label>
              <input
                type="file"
                accept=".csv,.json,.xml,.xls,.xlsx"
                onChange={(e) => { setFile(e.target.files?.[0] ?? null); setFileResult(null) }}
                className="block w-full text-xs text-subtle-light dark:text-subtle-dark file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary/5 file:text-primary hover:file:bg-primary/10 cursor-pointer"
              />
            </div>
            <button
              onClick={() => file && fileMutation.mutate(file)}
              disabled={!file || !profileId || fileMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              <span className={`material-icons text-sm ${fileMutation.isPending ? 'animate-spin' : ''}`}>
                {fileMutation.isPending ? 'sync' : 'upload_file'}
              </span>
              {fileMutation.isPending ? 'Importing…' : 'Import File'}
            </button>
            {fileError && <p className="text-xs text-error">{fileError}</p>}
            {fileResult && <ImportResultCard result={fileResult} />}
          </div>
        </Card>
      )}

      {/* JSON paste */}
      {method === 'json' && (
        <Card>
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-1">Paste JSON Payload</h3>
          <p className="text-xs text-subtle-light dark:text-subtle-dark mb-4">
            Paste or edit a <code className="font-mono">PQCImportEnvelope</code> with{' '}
            <code className="font-mono">inventories[]</code> and <code className="font-mono">sources[]</code>.
          </p>
          <div className="space-y-3">
            <textarea
              value={jsonBody}
              onChange={(e) => { setJsonBody(e.target.value); setJsonResult(null) }}
              rows={14}
              placeholder='{\n  "schema_version": "2.0",\n  "import_type": "inbound",\n  "inventories": [...],\n  "sources": [...]\n}'
              className="w-full px-3 py-2 text-xs font-mono rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setJsonBody(JSON.stringify({
                  schema_version: '2.0', import_type: 'inbound', inventories: [], sources: [],
                }, null, 2)); setJsonResult(null) }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors"
              >
                Reset to template
              </button>
              <button
                onClick={handleJsonImport}
                disabled={!jsonBody.trim() || !profileId || jsonMutation.isPending}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                <span className={`material-icons text-sm ${jsonMutation.isPending ? 'animate-spin' : ''}`}>
                  {jsonMutation.isPending ? 'sync' : 'data_object'}
                </span>
                {jsonMutation.isPending ? 'Importing…' : 'Import JSON'}
              </button>
            </div>
            {jsonError && <p className="text-xs text-error">{jsonError}</p>}
            {jsonResult && <ImportResultCard result={jsonResult} />}
          </div>
        </Card>
      )}
    </div>
  )
}

// ── Export tab ────────────────────────────────────────────────────────────────

function ExportTab({ outboundProfiles }: { outboundProfiles: CmdbOutboundProfile[] }) {
  const { currentOrgId: orgId } = useOrgStore()
  const [profileId, setProfileId] = useState('')
  const [format, setFormat] = useState<FileFormat>('csv')

  useEffect(() => {
    if (!profileId) {
      const generic = outboundProfiles.find(p => p.is_system && p.name === 'Generic')
      if (generic) setProfileId(generic.id)
    }
  }, [outboundProfiles, profileId])

  const downloadMutation = useMutation({
    mutationFn: () => cmdbApi.standaloneDownloadExport(orgId, format, profileId || undefined),
  })

  return (
    <div className="space-y-5">
      <Card>
        <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-4">Download Export</h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Format</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as FileFormat)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {(['csv', 'json', 'xml', 'xls', 'xlsx'] as FileFormat[]).map((f) => (
                <option key={f} value={f}>{f.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <ProfileSelect
            label="Outbound Profile *"
            profiles={outboundProfiles}
            value={profileId}
            onChange={setProfileId}
          />
        </div>
        <button
          onClick={() => downloadMutation.mutate()}
          disabled={!profileId || downloadMutation.isPending}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <span className="material-icons text-sm">download</span>
          {downloadMutation.isPending ? 'Downloading…' : `Download ${format.toUpperCase()}`}
        </button>
      </Card>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CmdbImportExport({ asTab = false }: { asTab?: boolean } = {}) {
  const { currentOrgId: orgId } = useOrgStore()
  const [tab, setTab] = useState<'import' | 'export'>('import')

  const { data: inboundResp } = useQuery({
    queryKey: ['cmdb-inbound-profiles', orgId],
    queryFn: () => cmdbApi.listProfiles(orgId, 'inbound'),
    enabled: !!orgId,
  })
  const { data: outboundResp } = useQuery({
    queryKey: ['cmdb-outbound-profiles', orgId],
    queryFn: () => cmdbApi.listProfiles(orgId, 'outbound'),
    enabled: !!orgId,
  })

  const inboundProfiles: CmdbInboundProfile[] = inboundResp ?? []
  const outboundProfiles: CmdbOutboundProfile[] = outboundResp ?? []

  return (
    <div className={asTab ? 'space-y-6' : 'p-6 max-w-4xl mx-auto space-y-6'}>
      {!asTab && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to="/cmdb"
                className="flex items-center gap-1 text-xs text-subtle-light dark:text-subtle-dark hover:text-primary transition-colors"
              >
                <span className="material-icons text-sm">arrow_back</span>
                CMDB
              </Link>
              <span className="text-subtle-light dark:text-subtle-dark">/</span>
              <h1 className="text-xl font-semibold text-text-light dark:text-text-dark">Import &amp; Export</h1>
            </div>
          </div>
          <p className="text-sm text-subtle-light dark:text-subtle-dark -mt-2">
            Bulk-import inventories and sources from a file or JSON payload, or export your current CMDB data.
            Bulk-import or download your CMDB data directly — no handler required.
          </p>
        </>
      )}

      {/* Tabs */}
      <div className="flex rounded-xl overflow-hidden w-fit">
        {(['import', 'export'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2 text-sm font-medium transition-colors capitalize ${
              tab === t
                ? 'bg-primary text-white'
                : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'import' ? (
        <ImportTab inboundProfiles={inboundProfiles} />
      ) : (
        <ExportTab outboundProfiles={outboundProfiles} />
      )}
    </div>
  )
}
